import React, { useCallback, useEffect, useRef, useState } from 'react';

const COMPLETION_THRESHOLD = 0.98;
const HEARTBEAT_INTERVAL_MS = 15_000;

export default function VideoPlayer({ src, onStart, onHeartbeat, onComplete, isCompleted }) {
  const videoRef = useRef(null);
  const maxWatchedRef = useRef(0);
  const startedRef = useRef(false);
  const completingRef = useRef(false);
  const lastHeartbeatRef = useRef(0);
  const isSeeking = useRef(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isDone, setIsDone] = useState(Boolean(isCompleted));
  const [showSkipWarning, setShowSkipWarning] = useState(false);
  const [serverError, setServerError] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    maxWatchedRef.current = 0;
    startedRef.current = false;
    completingRef.current = false;
    lastHeartbeatRef.current = 0;
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setIsDone(Boolean(isCompleted));
    setServerError('');
    setShowSkipWarning(false);
    if (videoRef.current) videoRef.current.load();
  }, [src, isCompleted]);

  const ensureStarted = useCallback(async () => {
    if (startedRef.current || isCompleted) return true;
    try {
      await onStart?.();
      startedRef.current = true;
      lastHeartbeatRef.current = Date.now();
      return true;
    } catch (error) {
      setServerError(error.response?.data?.error || 'Could not start this training module. Please retry.');
      videoRef.current?.pause();
      return false;
    }
  }, [isCompleted, onStart]);

  const sendHeartbeat = useCallback(async (positionSeconds, durationSeconds) => {
    if (!startedRef.current || isCompleted) return;
    try {
      await onHeartbeat?.({ positionSeconds, durationSeconds });
      setServerError('');
    } catch (error) {
      const allowed = Number(
        error.response?.data?.allowedPositionSeconds ??
        error.response?.data?.details?.allowedPositionSeconds
      );
      if (Number.isFinite(allowed) && videoRef.current) {
        videoRef.current.currentTime = allowed;
        maxWatchedRef.current = Math.min(maxWatchedRef.current, allowed);
        setCurrentTime(allowed);
      }
      setServerError(error.response?.data?.error || 'Progress could not be saved. Keep this page open while we reconnect.');
    }
  }, [isCompleted, onHeartbeat]);

  const reportCompletion = useCallback(async () => {
    const video = videoRef.current;
    if (!video || isDone || completingRef.current) return;
    completingRef.current = true;
    setVerifying(true);
    try {
      const accepted = await onComplete?.({
        positionSeconds: video.currentTime,
        durationSeconds: Number.isFinite(video.duration) ? video.duration : duration,
      });
      if (accepted === false) throw new Error('Server did not confirm completion');
      setIsDone(true);
      setServerError('');
    } catch (error) {
      setServerError(
        error.response?.data?.error ||
        'The server has not verified enough watch time yet. Continue watching through the end.'
      );
    } finally {
      setVerifying(false);
      completingRef.current = false;
    }
  }, [duration, isDone, onComplete]);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || isSeeking.current) return;
    const position = video.currentTime;
    const videoDuration = video.duration;
    if (position > maxWatchedRef.current) maxWatchedRef.current = position;
    setCurrentTime(position);

    if (startedRef.current && !video.paused && Date.now() - lastHeartbeatRef.current >= HEARTBEAT_INTERVAL_MS) {
      lastHeartbeatRef.current = Date.now();
      sendHeartbeat(position, Number.isFinite(videoDuration) ? videoDuration : 0);
    }

    if (Number.isFinite(videoDuration) && videoDuration > 0 && position / videoDuration >= COMPLETION_THRESHOLD) {
      reportCompletion();
    }
  }, [reportCompletion, sendHeartbeat]);

  const handleSeeking = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    isSeeking.current = true;
    if (!isCompleted && video.currentTime > maxWatchedRef.current + 1.5) {
      video.currentTime = maxWatchedRef.current;
    }
  }, [isCompleted]);

  const handleSeeked = useCallback(() => { isSeeking.current = false; }, []);

  const handleEnded = async () => {
    setIsPlaying(false);
    await sendHeartbeat(videoRef.current?.currentTime || 0, videoRef.current?.duration || duration);
    await reportCompletion();
  };

  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      if (!(await ensureStarted())) return;
      await video.play();
    } else {
      video.pause();
      await sendHeartbeat(video.currentTime, video.duration);
    }
  };

  const handleVolumeChange = (event) => {
    const value = Number.parseFloat(event.target.value);
    setVolume(value);
    if (videoRef.current) videoRef.current.volume = value;
    setIsMuted(value === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleProgressClick = (event) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const target = ((event.clientX - rect.left) / rect.width) * duration;
    if (isCompleted || target <= maxWatchedRef.current) {
      video.currentTime = target;
      setCurrentTime(target);
    } else {
      setShowSkipWarning(true);
      window.setTimeout(() => setShowSkipWarning(false), 2500);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || Number.isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${Math.floor(seconds % 60).toString().padStart(2, '0')}`;
  };

  const currentPercent = duration ? Math.min((currentTime / duration) * 100, 100) : 0;
  const watchedPercent = duration ? Math.min((maxWatchedRef.current / duration) * 100, 100) : 0;

  return (
    <div className="video-player-wrap" onContextMenu={(event) => event.preventDefault()}>
      <video
        ref={videoRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
        onSeeking={handleSeeking}
        onSeeked={handleSeeked}
        onPlay={async () => { setIsPlaying(true); await ensureStarted(); }}
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
        playsInline
      />

      {isDone && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ background: 'rgba(39,174,96,0.92)', color: '#fff', borderRadius: '50%', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 700 }}>✓</div>
        </div>
      )}

      {showSkipWarning && (
        <div style={{ position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)', background: 'rgba(231,76,60,0.92)', color: '#fff', padding: '8px 18px', borderRadius: 6, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', zIndex: 10 }}>
          ⛔ Forward skipping is not allowed — please watch the video in full.
        </div>
      )}

      <div className="watch-lock-notice">🔒 Server-tracked</div>
      <div className="video-controls">
        <div className="progress-bar-track" onClick={handleProgressClick} title="Click to rewind. Forward skipping is disabled.">
          <div className="progress-bar-watched" style={{ width: `${watchedPercent}%` }} />
          <div className="progress-bar-current" style={{ width: `${currentPercent}%` }} />
        </div>
        <div className="controls-row">
          <button className="ctrl-btn" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>{isPlaying ? '⏸' : '▶'}</button>
          <span className="time-display">{formatTime(currentTime)} / {formatTime(duration)}</span>
          <div className="volume-control">
            <button className="ctrl-btn" onClick={toggleMute} aria-label="Toggle mute">{isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}</button>
            <input className="volume-slider" type="range" min="0" max="1" step="0.05" value={isMuted ? 0 : volume} onChange={handleVolumeChange} aria-label="Volume" />
          </div>
          {(isDone || verifying) && (
            <span style={{ color: isDone ? '#2ecc71' : '#f6c344', fontWeight: 700, fontSize: 13, marginLeft: 'auto' }}>
              {isDone ? '✓ Verified complete' : 'Verifying…'}
            </span>
          )}
        </div>
        {serverError && <div style={{ color: '#FEB2B2', fontSize: 12, padding: '0 14px 10px' }}>{serverError}</div>}
      </div>
    </div>
  );
}
